// Round-half-away-from-zero (matches Python ROUND_HALF_UP and Postgres ROUND(numeric)).
//
// Uses string-based exponential notation rather than `value * multiplier` to avoid
// the IEEE-754 trap where e.g. `1.005 * 100 === 100.49999999999999` would round
// down to 100 instead of up to 101. JS's number parser handles "1.005e2" by shifting
// the decimal point in the literal — never doing the floating-point multiplication.
export function roundHalfUp(value: number, precision: number): number {
    if (!Number.isFinite(value)) {
        return value
    }

    // `Math.sign(value) || 1` treats +0, -0, and NaN as positive so they short-circuit
    // to the positive branch instead of inverting the sign of the result.
    const sign = Math.sign(value) || 1
    const absValue = Math.abs(value)

    // Values outside [1e-6, 1e21) stringify in scientific notation (e.g. "1.5e-10"),
    // which doesn't round-trip through the exponential-string trick. Fall back to FP math.
    // At those magnitudes, there's no half-cent boundary to worry about anyway: very
    // small values round to 0, very large values have no fractional part to round.
    if (absValue < 1e-6 || absValue >= 1e21) {
        const factor = 10 ** precision
        return Math.round(value * factor) / factor
    }

    const shifted = Math.round(Number(`${absValue}e${precision}`))
    return sign * Number(`${shifted}e${-precision}`)
}
